import { CheckIcon, ExternalLinkIcon, UsersIcon } from 'lucide-react'

import { Delegate } from '../logic/useDelegateDialog'
import { ActionsViewState } from '@/features/actions/types'
import { ActionsView } from '@/features/actions/views/ActionsView'
import { Badge } from '@/ui/atoms/badge/Badge'
import { Button } from '@/ui/atoms/button/Button'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { TileButton } from '@/ui/atoms/tile-button/TileButton'
import { links } from '@/ui/constants/links'
import { Alert } from '@/ui/molecules/alert/Alert'
import { BlockExplorerAddressLink } from '@/ui/molecules/block-explorer-address-link/BlockExplorerAddressLink'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface DelegateViewProps {
  delegates: Delegate[]
  isDelegatingToNotWhitelisted: boolean
  isSelectedDelegateAlreadySet: boolean
  actionsViewState: ActionsViewState
  onSkip: () => void
  skipText: string
}

export function DelegateView({
  delegates,
  actionsViewState,
  onSkip,
  skipText,
  isDelegatingToNotWhitelisted,
  isSelectedDelegateAlreadySet,
}: DelegateViewProps) {
  return (
    <div className="flex flex-col gap-2 md:gap-6">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <UsersIcon className="icon-size-sm icon-secondary" />
          <DialogTitle className="flex items-center gap-1">
            Delegate & boost your points{' '}
            <Info className="mt-1">
              Delegate your voting power and increase your Spark points earnings by 10%.{' '}
              <ExternalLink to={links.docs.governance}>Learn more</ExternalLink>
            </Info>
          </DialogTitle>
        </div>
        <div className="typography-body-3 text-secondary sm:text-[13px]">
          By delegating your voting power, you authorize the delegate to vote on Spark governance proposals with the
          power of your tokens on your behalf, while you retain full ownership of the tokens.
        </div>
      </div>
      <div className="grid auto-rows-fr grid-cols-1 gap-3 md:grid-cols-2">
        {delegates.map((delegate) => (
          <TileButton
            key={delegate.address}
            isSelected={delegate.isSelected}
            onClick={delegate.onClick}
            className="flex flex-col justify-between gap-2 md:gap-6"
            data-testid={testIds.dialog.delegate.delegateButton}
          >
            <div className="flex w-full items-start justify-between gap-2">
              {delegate.avatar && (
                <img
                  src={delegate.avatar}
                  alt={delegate.address}
                  className="size-6 shrink-0 overflow-hidden rounded-full sm:size-8"
                />
              )}
              {delegate.isEnablingPointsBoost ? (
                <Badge size="sm" appearance="soft" variant="brand">
                  Points Boost
                </Badge>
              ) : (
                <Badge size="sm" appearance="soft" variant="neutral" className="tracking-tighter! sm:tracking-normal">
                  No Points Boost
                </Badge>
              )}
            </div>
            <div className="flex flex-col items-start gap-1">
              <div className="typography-label-2 flex items-center gap-1 text-primary">
                {delegate.name}
                {delegate.isCurrentDelegate && <CheckIcon className="icon-size-xs icon-system-success-primary" />}
              </div>
              {delegate.isEnablingPointsBoost && (
                <BlockExplorerAddressLink
                  address={delegate.address}
                  compact
                  className={cn(
                    'sm:typography-label-3 text-[10px] text-secondary',
                    '[&>span]:gap-1 [&_svg]:size-3 sm:[&_svg]:size-4',
                  )}
                />
              )}
            </div>
          </TileButton>
        ))}
      </div>
      {isDelegatingToNotWhitelisted && (
        <Alert variant="warning">
          Currently, you are delegating to a non-whitelisted delegate. Under the current voting strategy, both your
          voting power and your delegate's will be counted as 0. No additional Spark Points will be rewarded. Read more
          about it{' '}
          <ExternalLink to={links.docs.governance} variant="underline" className="inline-flex items-center gap-1">
            here <ExternalLinkIcon className="icon-size-xxs" />
          </ExternalLink>
          .
        </Alert>
      )}
      {isSelectedDelegateAlreadySet ? (
        <Button onClick={onSkip} data-testid={testIds.dialog.delegate.skipButton}>
          {skipText}
        </Button>
      ) : (
        <ActionsView state={actionsViewState} actionsGridLayout="compact" allowChangeWhenActionsInProgress={false} />
      )}
    </div>
  )
}
